import '../sass/project.scss';

const DEFAULT_LAT = 52.231838;
const DEFAULT_LNG = 21.006724;
const DEFAULT_ZOOM = 6;
const FOCUSED_ZOOM = 14;
const AUTOCOMPLETE_MIN_LENGTH = 3;
const AUTOCOMPLETE_DEBOUNCE_MS = 250;
const COUNTRY_DEFAULT_CURRENCY = {
  PL: 'PLN',
  NO: 'NOK',
};

let activeMapElement = null;
let activeMap = null;
let activeMarker = null;
let suppressLocationInputReset = false;

function setFieldHelpState(wrapper, state) {
  const trigger = wrapper.querySelector('[data-field-help-trigger]');
  const content = wrapper.querySelector('[data-field-help-content]');
  if (!trigger || !content) {
    return;
  }

  content.hidden = state === 'closed';
  wrapper.dataset.helpState = state;
  wrapper.classList.toggle('is-open', state !== 'closed');
  trigger.setAttribute('aria-expanded', state !== 'closed' ? 'true' : 'false');
}

function openFieldHelp(wrapper, persistent = false) {
  setFieldHelpState(wrapper, persistent ? 'persistent' : 'temporary');
}

function closeFieldHelp(wrapper, force = false) {
  if (!force && wrapper.dataset.helpState === 'persistent') {
    return;
  }

  setFieldHelpState(wrapper, 'closed');
}

function bindFieldHelp(form) {
  const wrappers = form.querySelectorAll('[data-field-help]');
  if (!wrappers.length) {
    return;
  }

  wrappers.forEach((wrapper) => {
    if (wrapper.dataset.bound) {
      return;
    }

    wrapper.dataset.bound = 'true';
    setFieldHelpState(wrapper, 'closed');

    const trigger = wrapper.querySelector('[data-field-help-trigger]');
    if (!trigger) {
      return;
    }

    trigger.addEventListener('mouseenter', () => openFieldHelp(wrapper));
    trigger.addEventListener('focus', () => openFieldHelp(wrapper));
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      if (wrapper.dataset.helpState === 'persistent') {
        closeFieldHelp(wrapper, true);
        return;
      }

      document.querySelectorAll('[data-field-help][data-help-state="persistent"]').forEach((otherWrapper) => {
        if (otherWrapper !== wrapper) {
          closeFieldHelp(otherWrapper, true);
        }
      });
      openFieldHelp(wrapper, true);
    });

    wrapper.addEventListener('mouseenter', () => {
      if (wrapper.dataset.helpState !== 'persistent') {
        openFieldHelp(wrapper);
      }
    });

    wrapper.addEventListener('mouseleave', () => closeFieldHelp(wrapper));
    wrapper.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (!wrapper.contains(document.activeElement)) {
          closeFieldHelp(wrapper);
        }
      }, 0);
    });
  });

  if (!document.body.dataset.fieldHelpDismissBound) {
    document.body.dataset.fieldHelpDismissBound = 'true';
    document.addEventListener('click', (event) => {
      document.querySelectorAll('[data-field-help][data-help-state="persistent"]').forEach((wrapper) => {
        if (!wrapper.contains(event.target)) {
          closeFieldHelp(wrapper, true);
        }
      });
    });
  }
}

function setLocationStatus(form, message, isError = false) {
  const status = form.querySelector('#project-location-status');
  if (!status) {
    return;
  }

  status.textContent = message;
  status.classList.toggle('text-danger', isError);
}

function updateLocationFields(form, lat, lng, address) {
  const latInput = form.querySelector('#project-location-lat');
  const lngInput = form.querySelector('#project-location-lng');
  const queryInput = form.querySelector('#project-location-query');

  if (latInput) latInput.value = lat;
  if (lngInput) lngInput.value = lng;
  if (queryInput && address) {
    suppressLocationInputReset = true;
    queryInput.value = address;
    window.setTimeout(() => {
      suppressLocationInputReset = false;
    }, 0);
  }

  setLocationStatus(form, `Wybrane współrzędne: ${lat}, ${lng}`);
}

function bindCountryCurrency(form) {
  const countrySelect = form.querySelector('#project-country-code-input');
  const currencySelect = form.querySelector('#project-currency-code-input');
  if (!countrySelect || !currencySelect) {
    return;
  }

  const syncCurrency = (force = false) => {
    const defaultCurrency = COUNTRY_DEFAULT_CURRENCY[countrySelect.value] || COUNTRY_DEFAULT_CURRENCY.PL;
    if (!currencySelect.value || force) {
      currencySelect.value = defaultCurrency;
    }
  };

  if (!countrySelect.dataset.bound) {
    countrySelect.dataset.bound = 'true';
    countrySelect.addEventListener('change', () => syncCurrency(true));
  }

  syncCurrency();
}

function handleResolvedLocation(form, data) {
  updateLocationFields(form, data.lat, data.lng, data.address);
  renderMarker(data.lat, data.lng);
}

function renderMarker(lat, lng) {
  if (!activeMap) {
    return;
  }

  if (!activeMarker) {
    activeMarker = window.L.marker([lat, lng]).addTo(activeMap);
  } else {
    activeMarker.setLatLng([lat, lng]);
  }
  activeMap.setView([lat, lng], FOCUSED_ZOOM);
}

function parseCoordinate(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsedValue = Number.parseFloat(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

async function resolveLocation(form, params) {
  const resolveUrl = form.dataset.locationResolveUrl;
  if (!resolveUrl) {
    return null;
  }

  const url = new URL(resolveUrl, window.location.origin);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      url.searchParams.set(key, value);
    }
  });

  const response = await fetch(url.toString(), {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'same-origin',
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Nie udało się ustalić lokalizacji.');
  }
  return data;
}

async function fetchAutocompleteResults(form, query) {
  const autocompleteUrl = form.dataset.locationAutocompleteUrl;
  if (!autocompleteUrl) {
    return [];
  }

  const url = new URL(autocompleteUrl, window.location.origin);
  url.searchParams.set('query', query);

  const response = await fetch(url.toString(), {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'same-origin',
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Nie udało się pobrać podpowiedzi adresu.');
  }
  return data.results || [];
}

function hideAutocomplete(form) {
  const container = form.querySelector('#project-location-autocomplete');
  if (!container) {
    return;
  }

  container.hidden = true;
  container.innerHTML = '';
}

function renderAutocomplete(form, results) {
  const container = form.querySelector('#project-location-autocomplete');
  if (!container) {
    return;
  }

  if (!results.length) {
    hideAutocomplete(form);
    return;
  }

  container.innerHTML = '';
  results.forEach((result) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'project-location-suggestion';
    button.dataset.locationSuggestion = 'true';
    button.dataset.lat = result.lat;
    button.dataset.lng = result.lng;
    button.dataset.address = result.address;
    button.textContent = result.address;
    container.appendChild(button);
  });
  container.hidden = false;
}

function bindMap(form) {
  const mapElement = form.querySelector('#project-location-map');
  if (!mapElement || !window.L) {
    return;
  }

  if (activeMap && activeMapElement !== mapElement) {
    activeMap.remove();
    activeMap = null;
    activeMarker = null;
  }

  if (!activeMap) {
    activeMapElement = mapElement;
    activeMap = window.L.map(mapElement);
    window.L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(activeMap);

    activeMap.on('click', async (event) => {
      const lat = event.latlng.lat.toFixed(6);
      const lng = event.latlng.lng.toFixed(6);
      try {
        const data = await resolveLocation(form, { lat, lng });
        updateLocationFields(form, data.lat, data.lng, data.address);
        renderMarker(data.lat, data.lng);
      } catch (error) {
        updateLocationFields(form, lat, lng, form.querySelector('#project-location-query')?.value || '');
        renderMarker(lat, lng);
        setLocationStatus(form, error.message, true);
      }
    });
  }

  window.setTimeout(() => activeMap.invalidateSize(), 0);

  const initialLat = parseCoordinate(mapElement.dataset.lat);
  const initialLng = parseCoordinate(mapElement.dataset.lng);
  const hasInitialCoords = initialLat !== null && initialLng !== null;

  activeMap.setView(
    [hasInitialCoords ? initialLat : DEFAULT_LAT, hasInitialCoords ? initialLng : DEFAULT_LNG],
    hasInitialCoords ? FOCUSED_ZOOM : DEFAULT_ZOOM,
  );
  if (hasInitialCoords) {
    renderMarker(initialLat, initialLng);
  } else if (activeMarker) {
    activeMap.removeLayer(activeMarker);
    activeMarker = null;
  }
}

function bindProjectMetadata(container = document) {
  const form = container.querySelector('#project-metadata-form');
  if (!form) {
    if (activeMap) {
      activeMap.remove();
      activeMap = null;
      activeMarker = null;
      activeMapElement = null;
    }
    return;
  }

  bindMap(form);
  bindFieldHelp(form);
  bindCountryCurrency(form);

  const previewButton = form.querySelector('[data-location-preview]');
  if (previewButton && !previewButton.dataset.bound) {
    previewButton.dataset.bound = 'true';
    previewButton.addEventListener('click', async () => {
      const query = form.querySelector('#project-location-query')?.value?.trim() || '';
      if (!query) {
        setLocationStatus(form, 'Podaj adres lub współrzędne GPS.', true);
        return;
      }

      try {
        const data = await resolveLocation(form, { query });
        handleResolvedLocation(form, data);
      } catch (error) {
        setLocationStatus(form, error.message, true);
      }
    });
  }

  const currentLocationButton = form.querySelector('[data-location-current]');
  if (currentLocationButton && !currentLocationButton.dataset.bound) {
    currentLocationButton.dataset.bound = 'true';
    currentLocationButton.addEventListener('click', () => {
      if (!navigator.geolocation) {
        setLocationStatus(form, 'Ta przeglądarka nie obsługuje geolokalizacji.', true);
        return;
      }

      setLocationStatus(form, 'Pobieranie bieżącej lokalizacji...');
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude.toFixed(6);
          const lng = position.coords.longitude.toFixed(6);

          try {
            const data = await resolveLocation(form, { lat, lng });
            handleResolvedLocation(form, data);
          } catch (error) {
            updateLocationFields(form, lat, lng, form.querySelector('#project-location-query')?.value || '');
            renderMarker(lat, lng);
            setLocationStatus(form, error.message, true);
          }
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            setLocationStatus(form, 'Brak zgody na użycie bieżącej lokalizacji.', true);
            return;
          }

          if (error.code === error.POSITION_UNAVAILABLE) {
            setLocationStatus(form, 'Nie udało się ustalić bieżącej lokalizacji.', true);
            return;
          }

          if (error.code === error.TIMEOUT) {
            setLocationStatus(form, 'Upłynął czas oczekiwania na bieżącą lokalizację.', true);
            return;
          }

          setLocationStatus(form, 'Nie udało się pobrać bieżącej lokalizacji.', true);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    });
  }

  const queryInput = form.querySelector('#project-location-query');
  if (queryInput && !queryInput.dataset.bound) {
    queryInput.dataset.bound = 'true';
    let autocompleteTimeoutId = null;

    queryInput.addEventListener('input', () => {
      if (suppressLocationInputReset) {
        return;
      }

      const latInput = form.querySelector('#project-location-lat');
      const lngInput = form.querySelector('#project-location-lng');
      if (latInput) latInput.value = '';
      if (lngInput) lngInput.value = '';

      if (!queryInput.value.trim()) {
        hideAutocomplete(form);
        setLocationStatus(form, 'Możesz wpisać adres, współrzędne GPS albo wskazać punkt na mapie.');
        return;
      }

      const query = queryInput.value.trim();
      if (query.length < AUTOCOMPLETE_MIN_LENGTH || query.includes(',')) {
        hideAutocomplete(form);
        return;
      }

      window.clearTimeout(autocompleteTimeoutId);
      autocompleteTimeoutId = window.setTimeout(async () => {
        try {
          const results = await fetchAutocompleteResults(form, query);
          renderAutocomplete(form, results);
        } catch (_error) {
          hideAutocomplete(form);
        }
      }, AUTOCOMPLETE_DEBOUNCE_MS);
    });

    queryInput.addEventListener('blur', () => {
      window.setTimeout(() => hideAutocomplete(form), 150);
    });
  }

  const autocompleteContainer = form.querySelector('#project-location-autocomplete');
  if (autocompleteContainer && !autocompleteContainer.dataset.bound) {
    autocompleteContainer.dataset.bound = 'true';
    autocompleteContainer.addEventListener('click', (event) => {
      const suggestion = event.target.closest('[data-location-suggestion]');
      if (!suggestion) {
        return;
      }

      handleResolvedLocation(form, {
        lat: suggestion.dataset.lat,
        lng: suggestion.dataset.lng,
        address: suggestion.dataset.address,
      });
      hideAutocomplete(form);
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  bindProjectMetadata();
});

document.body.addEventListener('htmx:afterSwap', (event) => {
  bindProjectMetadata(event.target);
});
